import { hideBackdrop, showBackdrop } from "./backdrop-utils.js";
import { createHeaderSearchController } from "./header-search-controller.js";

const HEADER_SEARCH_OPEN_CLASS = "header--search-open";
const HEADER_SCROLLED_CLASS = "header--scrolled";
const LOGO_DARK_FILE = "logo-company-dark.svg";
const LOGO_LIGHT_FILE = "logo-company-white.svg";
const HEADER_SEARCH_ACTIVE_CLASS = "header__search--active";
const HEADER_NAV_HIDDEN_CLASS = "header__nav--hidden";
const HEADER_MENU_COLLAPSED_CLASS = "header--menu-collapsed";
const BODY_LOCK_CLASS = "is-locked";
const PAGE_OPEN_MENU_CLASS = "page--open-menu";
const BURGER_ACTIVE_CLASS = "burger--active";
const SEARCH_BACKDROP_KEY = "header-search";
const SEARCH_TOGGLE_ACTIVE_CLASS = "header__search-toggle--active";
const MENU_COLLAPSE_SCROLL_OFFSET = 80;
const MENU_COLLAPSE_SCROLL_DELTA = 8;
const HEADER_SCROLLED_OFFSET = 0;

const setSearchButtonState = (button, isOpen) => {
  button.classList.toggle(SEARCH_TOGGLE_ACTIVE_CLASS, isOpen);
  button.setAttribute("aria-expanded", String(isOpen));
  button.setAttribute("aria-pressed", String(isOpen));

  const showIcon = button.querySelector("[data-search-icon-show]");
  const hideIcon = button.querySelector("[data-search-icon-hide]");

  showIcon?.toggleAttribute("hidden", isOpen);
  hideIcon?.toggleAttribute("hidden", !isOpen);
};

export const initHeaderInteractions = (documentReference = document) => {
  const headerRoot = documentReference.querySelector("[data-header-root]");
  if (!headerRoot) {
    return;
  }

  const pageEl = headerRoot.closest(".page");
  const burgerButton = headerRoot.querySelector("[data-header-burger]");
  const openButton = headerRoot.querySelector("[data-header-search-open]");
  const headerNav = headerRoot.querySelector(".header__nav");
  const headerSearch = headerRoot.querySelector(".header__search");
  const searchInput = headerRoot.querySelector(".global-search__input");
  const searchClearButton = headerRoot.querySelector("[data-header-search-clear]");

  if (!openButton && !burgerButton) {
    return;
  }

  const searchRoot = documentReference.querySelector("[data-header-search]");
  const closeButtons = searchRoot ? searchRoot.querySelectorAll("[data-header-search-close]") : [];
  let isSearchOpen = false;
  let isMobileMenuOpen = false;
  let isMenuCollapsed = false;
  let lastScrollY = documentReference.defaultView?.scrollY || 0;
  let isHeaderScrolled = lastScrollY > HEADER_SCROLLED_OFFSET;
  const headerSearchController = createHeaderSearchController(documentReference, headerRoot);

  const canvasContext = documentReference.createElement("canvas").getContext("2d");
  let searchClearButtonFrameId = null;

  const getInputTextWidth = (inputValue = "") => {
    if (!searchInput || !canvasContext) {
      return 0;
    }

    const computedStyle = documentReference.defaultView?.getComputedStyle(searchInput);
    const fontSize = computedStyle?.fontSize || "1.6rem";
    const fontFamily = computedStyle?.fontFamily || "sans-serif";
    const fontWeight = computedStyle?.fontWeight || "400";
    const letterSpacing = Number.parseFloat(computedStyle?.letterSpacing || "0") || 0;
    const value = inputValue || searchInput.placeholder || "";

    canvasContext.font = `${fontWeight} ${fontSize} ${fontFamily}`;
    const measuredWidth = canvasContext.measureText(value).width;
    const spacingWidth = letterSpacing > 0 ? letterSpacing * Math.max(value.length - 1, 0) : 0;

    return measuredWidth + spacingWidth;
  };

  const updateSearchClearButton = () => {
    if (!searchInput || !searchClearButton) {
      return;
    }

    const value = searchInput.value || "";
    const hasValue = value.trim().length > 0;

    searchClearButton.classList.toggle("global-search__clear--visible", hasValue);

    if (!hasValue) {
      searchClearButton.style.removeProperty("--clear-offset");
      return;
    }

    const textWidth = getInputTextWidth(value);
    const maxOffset = Math.max(searchInput.clientWidth - 24, 0);
    const safeOffset = Math.min(Math.ceil(textWidth + 16), maxOffset);
    searchClearButton.style.setProperty("--clear-offset", `${safeOffset}px`);
  };

  const scheduleSearchClearButtonUpdate = () => {
    const view = documentReference.defaultView;

    if (!view) {
      updateSearchClearButton();
      return;
    }

    if (searchClearButtonFrameId !== null) {
      view.cancelAnimationFrame(searchClearButtonFrameId);
    }

    searchClearButtonFrameId = view.requestAnimationFrame(() => {
      searchClearButtonFrameId = view.requestAnimationFrame(() => {
        searchClearButtonFrameId = null;
        updateSearchClearButton();
      });
    });
  };

  const syncLightHeaderBrand = () => {
    if (!headerRoot.closest(".page--light")) {
      return;
    }

    const logo = headerRoot.querySelector(".header__brand-logo");
    if (!logo) {
      return;
    }

    const useDarkLogo =
      headerRoot.classList.contains(HEADER_SCROLLED_CLASS) ||
      headerRoot.classList.contains(HEADER_SEARCH_OPEN_CLASS) ||
      pageEl?.classList.contains(PAGE_OPEN_MENU_CLASS);
    const targetFile = useDarkLogo ? LOGO_DARK_FILE : LOGO_LIGHT_FILE;
    const currentSrc = logo.getAttribute("src") || "";

    if (currentSrc.includes("logo-company-") && !currentSrc.endsWith(targetFile)) {
      logo.src = currentSrc.replace(/logo-company-(dark|white)\.svg/i, targetFile);
    }
  };

  const updateHeaderScrollState = () => {
    headerRoot.classList.toggle(HEADER_SCROLLED_CLASS, isHeaderScrolled);
    syncLightHeaderBrand();
  };

  const setMenuCollapsed = (shouldCollapse) => {
    if (isMenuCollapsed === shouldCollapse) {
      return;
    }

    isMenuCollapsed = shouldCollapse;
    headerRoot.classList.toggle(HEADER_MENU_COLLAPSED_CLASS, shouldCollapse);
  };

  const syncBodyLock = () => {
    documentReference.body.classList.toggle(BODY_LOCK_CLASS, isSearchOpen || isMobileMenuOpen);
  };

  const setBurgerState = (isOpen) => {
    if (!burgerButton) {
      return;
    }

    burgerButton.classList.toggle(BURGER_ACTIVE_CLASS, isOpen);
  };

  const closeMobileMenu = () => {
    if (!isMobileMenuOpen) {
      return;
    }

    isMobileMenuOpen = false;
    pageEl?.classList.remove(PAGE_OPEN_MENU_CLASS);
    setBurgerState(false);
    syncBodyLock();
    syncLightHeaderBrand();
  };

  const openMobileMenu = () => {
    if (isMobileMenuOpen) {
      return;
    }

    if (isSearchOpen) {
      closeSearch();
    }

    isMobileMenuOpen = true;
    pageEl?.classList.add(PAGE_OPEN_MENU_CLASS);
    setBurgerState(true);
    syncBodyLock();
    syncLightHeaderBrand();
  };

  const toggleMobileMenu = () => {
    if (isMobileMenuOpen) {
      closeMobileMenu();
    } else {
      openMobileMenu();
    }
  };

  const closeSearch = () => {
    isSearchOpen = false;
    headerRoot.classList.remove(HEADER_SEARCH_OPEN_CLASS);
    headerSearch?.classList.remove(HEADER_SEARCH_ACTIVE_CLASS);
    headerNav?.classList.remove(HEADER_NAV_HIDDEN_CLASS);
    syncBodyLock();
    hideBackdrop(SEARCH_BACKDROP_KEY, documentReference);
    if (openButton) {
      setSearchButtonState(openButton, false);
    }

    syncLightHeaderBrand();
  };

  const openSearch = () => {
    closeMobileMenu();

    isSearchOpen = true;
    headerRoot.classList.add(HEADER_SEARCH_OPEN_CLASS);
    headerSearch?.classList.add(HEADER_SEARCH_ACTIVE_CLASS);
    headerNav?.classList.add(HEADER_NAV_HIDDEN_CLASS);
    syncBodyLock();
    showBackdrop(SEARCH_BACKDROP_KEY, documentReference);
    if (openButton) {
      setSearchButtonState(openButton, true);
    }

    syncLightHeaderBrand();
    headerSearchController.render?.();
    scheduleSearchClearButtonUpdate();
  };

  if (openButton) {
    setSearchButtonState(openButton, false);
  }

  updateHeaderScrollState();

  documentReference.addEventListener("nii:subscribe-modal-will-open", () => {
    closeSearch();
    closeMobileMenu();
  });

  burgerButton?.addEventListener("click", toggleMobileMenu);

  openButton?.addEventListener("click", () => {
    if (isSearchOpen) {
      closeSearch();
      return;
    }

    openSearch();
  });

  closeButtons.forEach((button) => {
    button.addEventListener("click", closeSearch);
  });

  searchInput?.addEventListener("input", updateSearchClearButton);
  searchInput?.addEventListener("focus", updateSearchClearButton);

  searchClearButton?.addEventListener("click", () => {
    if (!searchInput) {
      return;
    }

    searchInput.value = "";
    searchInput.dispatchEvent(new Event("input", { bubbles: true }));
    searchInput.focus();
    updateSearchClearButton();
    headerSearchController.render?.();
  });

  documentReference.defaultView?.addEventListener("resize", updateSearchClearButton, {
    passive: true
  });

  documentReference.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") {
      return;
    }

    if (isSearchOpen) {
      closeSearch();
      return;
    }

    closeMobileMenu();
  });

  documentReference.defaultView?.addEventListener(
    "scroll",
    () => {
      const currentScrollY = documentReference.defaultView?.scrollY || 0;
      const scrollDifference = currentScrollY - lastScrollY;
      isHeaderScrolled = currentScrollY > HEADER_SCROLLED_OFFSET;
      updateHeaderScrollState();

      if (isSearchOpen) {
        lastScrollY = currentScrollY;
        return;
      }

      if (currentScrollY <= MENU_COLLAPSE_SCROLL_OFFSET) {
        setMenuCollapsed(false);
        lastScrollY = currentScrollY;
        return;
      }

      if (Math.abs(scrollDifference) < MENU_COLLAPSE_SCROLL_DELTA) {
        return;
      }

      if (scrollDifference > 0) {
        setMenuCollapsed(true);
      } else {
        setMenuCollapsed(false);
      }

      lastScrollY = currentScrollY;
    },
    { passive: true }
  );

  updateSearchClearButton();
  headerSearchController.render?.();
};
